<template>
  <div :class="[s.multi_select, size === 'small' && s.small ]" role="group" aria-label="複数選択可能な項目">
    <div :class="s.buttons">
      <div
        v-for="(option, index) in options"
        :key="option.value"
        :class="[s.button_wrap, size !== 'small' && s.button_wrap_normal, isSelected(option.value) && s.isActive]"
      >
        <button
          type="button"
          role="checkbox"
          :aria-checked="isSelected(option.value) ? 'true' : 'false'"
          :class="[s.button, size === 'small' ? s.button_small : s.button_normal,
          isSelected(option.value) ? s.active : '', option?.descriptions?.length > 0 && s.hasDesc]"
          @click="toggleOption(option.value)"
        >
          <img
            v-if="size !== 'small'"
            :src="`/img/pages/enquete/a${index + 1}.svg`"
            :class="s.icon"
            alt=""
          >
          {{ option.label }}
          <ul v-if="option?.descriptions?.length > 0" :class="s.desc">
            <li v-for="description in option.descriptions" :key="description">{{description}}</li>
          </ul>
        </button>
      </div>
      <!-- その他選択肢（構造変更: div + 背面button） -->
      <div
        v-if="hasOtherOption"
        :class="[s.button_wrap, size !== 'small' && s.button_wrap_normal ]"
      >
        <div
          :class="[
            s.button_wrapper,
            size === 'small' ? s.button_small : s.button_normal,
            isOtherSelected ? s.active : ''
          ]"
        >
          <img
            v-if="size !== 'small'"
            :src="`/img/pages/enquete/a${options.length + 1}.svg`"
            :class="s.icon"
            alt=""
          >
          <!-- 背面のボタン（クリック可能領域） -->
          <button
            type="button"
            role="checkbox"
            :aria-checked="isOtherSelected ? 'true' : 'false'"
            :class="s.button_bg"
            @click="toggleOption('その他')"
          >
            <div :class="s.button_bg_inner">その他</div>
          </button>

          <!-- 前面のテキストエリア（選択時のみ表示） -->
          <div v-if="isOtherSelected" :class="s.other_input_container">
            <textarea
              v-model="otherText"
              :class="s.other_input"
              placeholder="その他の内容を入力してください"
              aria-label="その他の内容を入力"
              rows="3"
              @input="updateOtherValue"
              @click.stop
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- hidden input for Google Form (カンマ区切り文字列) -->
    <input
      type="hidden"
      :name="entryId"
      :value="hiddenValue"
    />
  </div>
</template>

<script>
export default {
  name: 'MultiSelectButtons',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      required: true,
      validator: (value) => value.every((opt) => opt.value && opt.label),
    },
    entryId: {
      type: String,
      required: true,
    },
    hasOtherOption: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['normal', 'small'].includes(value),
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      otherText: '',
      otherInputTimer: null,
    };
  },
  computed: {
    isOtherSelected() {
      return this.modelValue.some((val) => val === 'その他' || (val && val.startsWith('その他: ')));
    },
    hiddenValue() {
      // 配列をカンマ区切り文字列に変換
      const values = this.modelValue.map((val) => {
        if (val === 'その他' && this.otherText) {
          return `その他: ${this.otherText}`;
        }
        return val;
      });
      return values.join(', ');
    },
  },
  watch: {
    modelValue: {
      handler(newValue) {
        // 外部からの値変更を検知して「その他」テキストを復元
        const otherValue = newValue.find((val) => val && val.startsWith('その他: '));
        if (otherValue) {
          this.otherText = otherValue.replace('その他: ', '');
        } else if (!newValue.includes('その他')) {
          this.otherText = '';
        }
      },
      deep: true,
    },
  },
  beforeUnmount() {
    // タイマーのクリーンアップ
    if (this.otherInputTimer) {
      clearTimeout(this.otherInputTimer);
    }
  },
  methods: {
    isSelected(value) {
      return this.modelValue.includes(value);
    },
    toggleOption(value) {
      const newValue = [...this.modelValue];
      const index = newValue.findIndex((v) => v === value || (v && v.startsWith(`${value}: `)));

      if (value === 'その他') {
        if (index > -1) {
          // その他を解除
          newValue.splice(index, 1);
          this.otherText = '';
          // タイマーをクリア
          if (this.otherInputTimer) {
            clearTimeout(this.otherInputTimer);
          }
        } else {
          // その他を追加
          newValue.push('その他');
        }
      } else if (index > -1) {
        // 選択解除
        newValue.splice(index, 1);
      } else {
        // 選択追加
        newValue.push(value);
      }

      this.$emit('update:modelValue', newValue);
    },
    updateOtherValue() {
      // デバウンス処理: 入力応答性を向上
      if (this.otherInputTimer) {
        clearTimeout(this.otherInputTimer);
      }
      this.otherInputTimer = setTimeout(() => {
        const newValue = this.modelValue.filter((v) => v !== 'その他' && !v?.startsWith('その他: '));
        if (this.otherText) {
          newValue.push(`その他: ${this.otherText}`);
        } else {
          newValue.push('その他');
        }
        this.$emit('update:modelValue', newValue);
      }, 300);
    },
  },
};
</script>

<style lang="scss" module="s">
.multi_select {
  width: 100%;
  margin-top: 80px;

  &.small {
    margin-top: 0;
  }
}

.buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 24px;
}

.button_wrap {
  position: relative;
  z-index: 1;

  &.button_wrap_normal {
    width: 100%;
  }

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: #fff;
    border: 3px solid #000;
    border-radius: 43px;
    translate: 11px 13px;
    transition: all 0.2s ease;
    background: var(--primary-color, #4CAF50) url(/img/pages/choikulove/bg-dot.svg) repeat;
    background-size: 10px;
  }

  &:active::before {
    translate: 0 0;
  }
}

.button {
  position: relative;
  z-index: 2;
  font-weight: 500;
  color: #000;
  background-color: #fff;
  border: 3px solid #000;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 26px;

  &:hover {
    translate: 0 -5px;
  }

  &:active {
    translate: 0 0;
  }

  &.active {
    background-color: var(--primary-color, #4CAF50);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2);
  }
}

.button_normal {
  width: 100%;
  border-radius: 43px;
  min-height: 86px;
  padding: 20px 80px;

  &.hasDesc {
    text-align: left;
    padding-left: 20%;
  }
}

.button_small {
  min-width: 325px;
  width: fit-content;
  height: 64px;
  border-radius: 32px;
}

.icon {
  position: absolute;
  top: 13px;
  left: 20px;
  display: block;
  width: 55px;
  height: auto;
}

.desc {
  margin-top: 8px;
  padding-left: 4px;
  font-size: 0.7em;
  font-weight: 600;
  letter-spacing: 0.5px;
}


// その他ボタンのラッパー（divに適用）
.button_wrapper {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  color: #333;
  background-color: #fff;
  border: 3px solid #000;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 26px;

  &:hover {
    translate: 0 -5px;
  }

  &:active {
    translate: 0 0;
  }

  &.active {
    background-color: var(--primary-color, #4CAF50);
  }

  // normalサイズ用のスタイル
  &.button_normal {
    width: 100%;
    border-radius: 43px;
    min-height: 86px;
    padding: 12px 80px;
  }

  // smallサイズ用のスタイル
  &.button_small {
    min-width: 325px;
    width: fit-content;
    min-height: 64px;
    border-radius: 32px;
    padding: 12px 20px;
  }
}

// 背面のボタン（クリック可能領域）
.button_bg {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  border-radius: inherit;

  &:focus {
    outline: none;
  }
}

.button_bg_inner {
  height: 100%;
  padding-top: 18px;
}

// テキストエリアのコンテナ（前面）
.other_input_container {
  position: relative;
  z-index: 2;
  width: 100%;
  margin-top: 56px;
  animation: slideDown 0.2s ease;
}

.other_input {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  color: #333;
  background-color: #fff;
  border: 3px solid #000;
  border-radius: 8px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: var(--primary-color, #4CAF50);
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  }

  &::placeholder {
    color: #999;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    translate: 0 -3px;
  }
  to {
    opacity: 1;
    translate: 0 0;
  }
}

// タブレット対応
@include mdView {
  .button_normal,
  .button_normal.button_wrapper {
    min-width: 100px;
    padding: 10px 16px 10px 60px;
    font-size: 20px;
  }

  .button_small {
    min-width: 250px;
    padding: 12px 20px;
    font-size: 20px;
  }

  .other_input {
    font-size: 15px;
    min-height: 70px;
    padding: 10px 16px 10px 60px;
  }
}

// スマートフォン対応
@include smView {
  .button_wrap::before {
    translate: 8px 9px;
  }

  .icon {
    top: 15px;
    left: 15px;
    width: 46px;
  }

  .button_normal,
  .button_normal.button_wrapper {
    min-width: 80px;
    padding: 10px 30px 10px 74px;
  }

  .button_small {
    min-width: 90px;
    padding: 8px 12px;
    font-size: 13px;
  }

  .other_input {
    padding: 10px;
    font-size: 14px;
    min-height: 60px;
  }
}

// 小型スマートフォン対応
@include xsView {
  .multi_select {
    margin-top: 30px;
  }

  .icon {
    top: 10px;
    left: 10px;
    width: 36px;
  }

  .button_normal,
  .button_normal.button_wrapper {
    padding: 0 28px 0 57px;
    font-size: 15px;
    min-height: 65px;

    &.hasDesc {
      padding-block: 10px;
    }
  }

  .button_wrap {
    width: 100%;
  }

  .button_small {
    padding: 8px 10px;
    font-size: 16px;
    min-height: 60px;
    width: 100%;
  }
}
</style>
